/**
 * The conversation: the person's messages, the assistant's answers (with the
 * steps it took and any web sources), and — when it waits on an action set
 * to "Ask" — the approval card (Allow once / Deny).
 */

import { useEffect, useRef, type ReactNode } from 'react';

import type { DisplayItem, PendingAction } from './assistant-api.ts';
import { Markdown } from './Markdown.tsx';

function describe(action: PendingAction): string {
  const input = action.input;
  const named =
    typeof input.name === 'string'
      ? `: ${input.name}`
      : typeof input.title === 'string'
        ? `: ${input.title}`
        : '';
  return `${action.label}${named}`;
}

export function AssistantThread(props: {
  messages: DisplayItem[];
  pending: PendingAction[] | null;
  busy: boolean;
  canDecide: boolean;
  onDecide: (decision: 'ALLOW' | 'DENY') => void;
}): ReactNode {
  const end = useRef<HTMLDivElement>(null);
  useEffect(
    () => end.current?.scrollIntoView({ block: 'end' }),
    [props.messages.length, props.busy, props.pending],
  );
  return (
    <div className="as-thread" aria-live="polite">
      {props.messages.map((item, index) => (
        <article key={index} className={`as-msg as-msg-${item.role}`}>
          {item.tools.length > 0 && (
            <ul className="as-steps" aria-label="Steps taken">
              {[...new Set(item.tools)].map((tool) => (
                <li key={tool}>{tool}</li>
              ))}
            </ul>
          )}
          {item.role === 'assistant' ? <Markdown text={item.text} /> : <p>{item.text}</p>}
          {item.sources.length > 0 && (
            <p className="as-sources">
              {item.sources.length} Source(s):{' '}
              {item.sources.slice(0, 5).map((source, at) => (
                <a key={source.url} href={source.url} target="_blank" rel="noreferrer noopener">
                  {at + 1}
                </a>
              ))}
            </p>
          )}
        </article>
      ))}
      {props.pending && props.pending.length > 0 && (
        <div
          className="as-approve"
          role="alertdialog"
          aria-label="SynAssistant asks for permission"
        >
          <strong>SynAssistant wants to:</strong>
          <ul>
            {props.pending.map((action) => (
              <li key={action.tool}>{describe(action)}</li>
            ))}
          </ul>
          {props.canDecide ? (
            <div className="as-approve-actions">
              <button
                type="button"
                className="btn"
                disabled={props.busy}
                onClick={() => props.onDecide('ALLOW')}
              >
                Allow once
              </button>
              <button
                type="button"
                className="btn secondary"
                disabled={props.busy}
                onClick={() => props.onDecide('DENY')}
              >
                Deny
              </button>
            </div>
          ) : (
            <p className="kp-faint">Only someone who can change reports may allow this.</p>
          )}
        </div>
      )}
      {props.busy && <p className="as-thinking">Thinking…</p>}
      <div ref={end} />
    </div>
  );
}
